import {
  EnterpriseApplicationStatus,
  SelectablePackageQuote,
  ReferralEnterpriseLeadApplicationsQuery,
  SelectQuoteForOpportunityMutationVariables,
} from '@graphql';

type TransformSelectQuoteForOpportunityInput = {
  selectedQuotes: SelectablePackageQuote[];
  isCashPayment?: boolean;
  confirmRegenerate?: boolean;
};

export const transformSelectQuoteForOpportunityInput = ({
  selectedQuotes,
  isCashPayment = false,
  confirmRegenerate = false,
}: TransformSelectQuoteForOpportunityInput): SelectQuoteForOpportunityMutationVariables | null => {
  const [mainQuote, ...relatedQuotes] = selectedQuotes;
  const opportunityId = mainQuote.opportunity?.id;

  if (!mainQuote.trackingId || !opportunityId) {
    return null;
  }

  const relatedQuotesWithTrackingIds = relatedQuotes.reduce<string[]>((acc, quote) => {
    if (quote.trackingId) {
      acc.push(quote.trackingId);
    }
    return acc;
  }, []);

  return {
    input: {
      opportunityId,
      trackingIds: [mainQuote.trackingId, ...relatedQuotesWithTrackingIds],
      isCashPayment,
      ...(confirmRegenerate && { confirmRegenerate }),
    },
  };
};

type ExistingLeadApplication = NonNullable<
  NonNullable<ReferralEnterpriseLeadApplicationsQuery['referral']['enterpriseLead']>['applications']
>[number];

export type ExistingApplicationBlockReason = 'active';

// Active statuses that block a new selection (shown with the "cancel via CommBank" message).
// QUOTE is temporarily non-blocking (INT-1396). CLOSED is permanently non-blocking (INT-1403).
// DECLINED / WITHDRAWN are dead and don't block. `status` is a plain string, hence string[].
// TODO: add 'SUBMITTED' here once it exists in EnterpriseApplicationStatus.
const ACTIVE_BLOCKING_STATUSES: string[] = [
  EnterpriseApplicationStatus.IN_PROGRESS,
  EnterpriseApplicationStatus.APPROVED,
  EnterpriseApplicationStatus.FUNDING,
  EnterpriseApplicationStatus.FUNDED,
];

// Blocking reason for the modal, or null to proceed. Scoped to the active package (its id is the
// enterprise orderId), so an application in another order/category never blocks.
export const getExistingApplicationBlockReason = (
  applications: ExistingLeadApplication[] | undefined,
  activePackageId: string | undefined,
): ExistingApplicationBlockReason | null => {
  if (!applications?.length || !activePackageId) {
    return null;
  }

  const packageApplications = applications.filter(application => application.orderId === activePackageId);

  if (packageApplications.some(application => ACTIVE_BLOCKING_STATUSES.includes(application.status))) {
    return 'active';
  }

  return null;
};
